import React from "react";
import {
  CreditCard,
  Edit,
  FilePlus2,
  FileDown,
  DollarSign,
} from "lucide-react";
import { useDispatch } from "react-redux";
import MainTable from "../../../../../components/mainTable";
import { TableColumnsItem } from "../../../../../interfaces/components/mainTable";
import {
  ColumnType,
  ButtonType,
  Status,
  ModalType,
} from "../../../../../shared/utils/enums";
import CurrencyDisplay from "../../../../../components/currencyDisplay";
import StatusChip from "../../../../../components/statusChip";
import {
  Invoice,
  InvoiceStatus,
} from "../../../../../interfaces/screens/billing";
import { Patient } from "../../../../../interfaces/screens/content/patientDetails";
import {
  showModal,
  hideModal,
} from "../../../../../connected-components/modal/reducer";
import InvoiceEditor from "../../../../../components/invoiceEditor";
import ClaimEditor from "../../../../../components/claimEditor";
import { showPaymentModal } from "../../../../../components/paymentModal";
import { store } from "../../../../../redux/store";
import { mapInvoiceStatusToChipStatus } from "../../../../../shared/utils/helper";
import ExportInvoiceModal from "../../../../../components/exportInvoiceModal";
import { generateInvoicePdf } from "../../../../../shared/utils/pdf";
import logo from "../../../../../assets/images/icons/logo-white.svg";
import Button from "../../../../../components/button";
import { ButtonVariant } from "../../../../../shared/utils/enums";

// Static data for patient billing records
const mockPatientBillingRecords: Invoice[] = [
  {
    id: "1",
    invoice_number: "INV-2024-001",
    patient_id: "patient-123",
    status: "paid" as InvoiceStatus,
    issue_date: "2024-03-15",
    due_date: "2024-04-15",
    exchange_rate_lbp_usd: 89000,
    notes_internal: "Annual physical examination",
    created_by_user_id: "user-1",
    line_items: [
      {
        id: "li-1",
        invoice_id: "1",
        description: "Annual Physical Examination",
        quantity: 1,
        unit_price: 250.0,
        currency: "USD",
        cpt_code: "99396",
        icd_code: "Z00.00",
        nssf_coverage_percentage: 80,
        moph_coverage_percentage: 100,
        requires_preauthorization: false,
      },
    ],
    payments: [
      {
        id: "pay-1",
        invoice_id: "1",
        payment_date: "2024-03-20",
        amount: 250.0,
        currency_paid: "USD",
        payment_method: "card_usd_fresh",
        reference_number: "REF123456",
        exchange_rate_at_payment: 89000,
      },
    ],
    total_amount: 250.0,
    balance_due: 0.0,
    is_insured: false,
    requires_claim_submission: false,
  },
  {
    id: "2",
    invoice_number: "INV-2024-002",
    patient_id: "patient-123",
    status: "pending_insurance" as InvoiceStatus,
    issue_date: "2024-03-10",
    due_date: "2024-04-10",
    exchange_rate_lbp_usd: 89000,
    notes_internal: "Blood work and lab tests",
    created_by_user_id: "user-1",
    line_items: [
      {
        id: "li-2",
        invoice_id: "2",
        description: "Blood Work - Complete Panel",
        quantity: 1,
        unit_price: 180.0,
        currency: "USD",
        cpt_code: "80053",
        icd_code: "Z01.818",
        nssf_coverage_percentage: 70,
        moph_coverage_percentage: 100,
        requires_preauthorization: false,
      },
    ],
    payments: [],
    total_amount: 180.0,
    balance_due: 180.0,
    is_insured: true,
    insurance_type: "nssf",
    requires_claim_submission: true,
    claim_deadline: "2024-06-10",
  },
  {
    id: "3",
    invoice_number: "INV-2024-003",
    patient_id: "patient-123",
    status: "paid" as InvoiceStatus,
    issue_date: "2024-02-28",
    due_date: "2024-03-28",
    exchange_rate_lbp_usd: 89000,
    notes_internal: "Follow-up consultation",
    created_by_user_id: "user-1",
    line_items: [
      {
        id: "li-3",
        invoice_id: "3",
        description: "Follow-up Consultation",
        quantity: 1,
        unit_price: 120.0,
        currency: "USD",
        cpt_code: "99213",
        icd_code: "Z09",
        nssf_coverage_percentage: 80,
        moph_coverage_percentage: 100,
        requires_preauthorization: false,
      },
    ],
    payments: [
      {
        id: "pay-2",
        invoice_id: "3",
        payment_date: "2024-03-05",
        amount: 120.0,
        currency_paid: "USD",
        payment_method: "cash_usd",
        reference_number: "REF123457",
        exchange_rate_at_payment: 89000,
      },
    ],
    total_amount: 120.0,
    balance_due: 0.0,
    is_insured: false,
    requires_claim_submission: false,
  },
  {
    id: "4",
    invoice_number: "INV-2024-004",
    patient_id: "patient-123",
    status: "issued" as InvoiceStatus,
    issue_date: "2024-02-15",
    due_date: "2024-03-15",
    exchange_rate_lbp_usd: 89000,
    notes_internal: "Chest X-ray examination",
    created_by_user_id: "user-1",
    line_items: [
      {
        id: "li-4",
        invoice_id: "4",
        description: "X-Ray - Chest",
        quantity: 1,
        unit_price: 150.0,
        currency: "USD",
        cpt_code: "71045",
        icd_code: "R91.8",
        nssf_coverage_percentage: 60,
        moph_coverage_percentage: 100,
        requires_preauthorization: true,
        preauthorization_number: "PRE-AUTH-001",
      },
    ],
    payments: [],
    total_amount: 150.0,
    balance_due: 150.0,
    is_insured: true,
    insurance_type: "private",
    requires_claim_submission: true,
    claim_deadline: "2024-05-15",
  },
  {
    id: "5",
    invoice_number: "INV-2024-005",
    patient_id: "patient-123",
    status: "partially_paid" as InvoiceStatus,
    issue_date: "2024-01-20",
    due_date: "2024-02-20",
    exchange_rate_lbp_usd: 89000,
    notes_internal: "MRI Brain Scan",
    created_by_user_id: "user-1",
    line_items: [
      {
        id: "li-5",
        invoice_id: "5",
        description: "MRI Brain Scan",
        quantity: 1,
        unit_price: 500.0,
        currency: "USD",
        cpt_code: "70553",
        icd_code: "R41.9",
        nssf_coverage_percentage: 60,
        moph_coverage_percentage: 100,
        requires_preauthorization: true,
        preauthorization_number: "PRE-AUTH-002",
      },
    ],
    payments: [
      {
        id: "pay-3",
        invoice_id: "5",
        payment_date: "2024-02-01",
        amount: 200.0,
        currency_paid: "USD",
        payment_method: "insurance_coverage",
        reference_number: "INS-REF-001",
        exchange_rate_at_payment: 89000,
      },
    ],
    total_amount: 500.0,
    balance_due: 300.0,
    is_insured: true,
    insurance_type: "private",
    requires_claim_submission: true,
    claim_deadline: "2024-04-20",
  },
];

export const BillingTab: React.FC<{ patient: Patient }> = ({ patient }) => {
  const dispatch = useDispatch();

  const handleNewBillingRecord = () => {
    dispatch(
      showModal({
        title: "Create New Billing Record",
        type: ModalType.COMPONENT,
        component: (
          <InvoiceEditor
            key="new-invoice"
            onClose={() => store.dispatch(hideModal())}
            patientId={String(patient.id)}
          />
        ),
      }),
    );
  };

  const handleEditBillingRecord = (invoice: Invoice) => {
    dispatch(
      showModal({
        title: `Edit Billing Record ${invoice.invoice_number}`,
        type: ModalType.COMPONENT,
        component: (
          <InvoiceEditor
            key={invoice.id}
            invoice={invoice}
            onClose={() => store.dispatch(hideModal())}
          />
        ),
      }),
    );
  };

  const handleGenerateClaim = (invoice: Invoice) => {
    dispatch(
      showModal({
        title: `New Claim for Invoice ${invoice.invoice_number}`,
        type: ModalType.COMPONENT,
        component: (
          <ClaimEditor
            key={`new-claim-${invoice.id}`}
            invoice={invoice}
            onClose={() => store.dispatch(hideModal())}
          />
        ),
      }),
    );
  };

  const handleAddPayment = (invoice: Invoice) => {
    showPaymentModal(invoice, (paymentData) => {
      console.log("Payment submitted:", paymentData);
      // In a real app, this would dispatch an action to save the payment
      alert(
        `Payment of ${paymentData.amount} ${paymentData.currency_paid} added to invoice ${invoice.invoice_number}`,
      );
    });
  };

  const handleExportPdf = async (invoice: Invoice) => {
    try {
      const response = await fetch(logo);
      const logoSvg = await response.text();
      generateInvoicePdf(invoice, logoSvg);
    } catch (error) {
      console.error("Error loading logo for PDF:", error);
      alert("Could not generate PDF. See console for details.");
    } finally {
      store.dispatch(hideModal());
    }
  };

  const handleOpenExportModal = (invoice: Invoice) => {
    dispatch(
      showModal({
        title: `Export Invoice ${invoice.invoice_number}`,
        type: ModalType.COMPONENT,
        width: "40%",
        component: (
          <ExportInvoiceModal
            invoice={invoice}
            onClose={() => store.dispatch(hideModal())}
            onExport={() => handleExportPdf(invoice)}
          />
        ),
      }),
    );
  };

  const columns: TableColumnsItem[] = [
    { key: "invoice_number", name: "Invoice #", type: ColumnType.TEXT },
    { key: "issue_date", name: "Issue Date", type: ColumnType.TEXT },
    { key: "due_date", name: "Due Date", type: ColumnType.TEXT },
    {
      key: "status",
      name: "Status",
      type: ColumnType.CUSTOM,
      customValue: (row: Invoice) => (
        <StatusChip status={mapInvoiceStatusToChipStatus(row.status)} />
      ),
    },
    {
      key: "total_amount",
      name: "Total",
      type: ColumnType.CUSTOM,
      customValue: (row: Invoice) => (
        <CurrencyDisplay amount={row.total_amount} currency={"USD"} />
      ),
    },
    {
      key: "balance_due",
      name: "Balance",
      type: ColumnType.CUSTOM,
      customValue: (row: Invoice) => (
        <CurrencyDisplay amount={row.balance_due} currency={"USD"} />
      ),
    },
    {
      key: "actions",
      name: "",
      type: ColumnType.BUTTON,
      buttonsList: [
        {
          type: ButtonType.ICON,
          icon: Edit,
          onPress: (e: React.MouseEvent, row: Invoice) =>
            handleEditBillingRecord(row),
        },
        {
          type: ButtonType.ICON,
          icon: DollarSign,
          onPress: (e: React.MouseEvent, row: Invoice) => handleAddPayment(row),
        },
        {
          type: ButtonType.ICON,
          icon: FilePlus2,
          onPress: (e: React.MouseEvent, row: Invoice) =>
            handleGenerateClaim(row),
        },
        {
          type: ButtonType.ICON,
          icon: FileDown,
          onPress: (e: React.MouseEvent, row: Invoice) =>
            handleOpenExportModal(row),
        },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium dark:text-white">
          Billing & Coding
        </h2>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={handleNewBillingRecord}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Add Billing Record
        </Button>
      </div>

      <MainTable
        data={mockPatientBillingRecords}
        columns={columns}
        showSearch={true}
        showAddButton={false}
        onRowPress={(row: Invoice) => handleEditBillingRecord(row)}
      />
    </div>
  );
};
